// Exercise Plan API Service
// This file contains functions to interact with the exercise plan API
import config from '../config/config.js'
import { apiService } from './api.js'

/**
 * Get authentication token
 * @returns {string} Auth token
 */
const getAuthToken = () => {
  return localStorage.getItem(config.storage.authToken) || ''
}

/**
 * Get company URL from stored user data
 * @returns {string} Company URL
 */
const getCompanyUrl = () => {
  return apiService.getCompanyUrl()
}

/**
 * Fetch exercise category tab tree from API
 * @returns {Promise<Object>} Exercise category tree object
 */
export const fetchExerciseCategoryTabTree = async () => {
  try {
    const companyUrl = getCompanyUrl()
    const baseURL = config.api.baseURL
    const url = `${baseURL}/${companyUrl}/exercise/getexercisecategorytabtree`
    
    console.log('🔄 Fetching exercise category tab tree:', {
      url,
      companyUrl,
      baseURL
    })

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      }
    })

    console.log('📡 API Response status:', response.status)

    if (!response.ok) {
      const errorText = await response.text()
      console.error('❌ API Error response:', errorText)
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`)
    }

    const data = await response.json()
    console.log('✅ Exercise category tab tree loaded:', data)
    return data
  } catch (error) {
    console.error('❌ Error fetching exercise category tab tree:', error)
    throw new Error(`Failed to fetch exercise category tab tree: ${error.message}`)
  }
}

/**
 * Fetch session exercise plans from API
 * @param {string} sessionId - Session ID
 * @param {string} clientId - Client ID
 * @returns {Promise<Array>} Array of session exercise plan objects
 */
export const fetchSessionExercisePlans = async (sessionId, clientId) => {
  try {
    const companyUrl = getCompanyUrl()
    const baseURL = config.api.baseURL
    const url = `${baseURL}/${companyUrl}/exercise/getsessionexerciseplans`
    
    const payload = {
      session_id: sessionId,
      client_id: clientId
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      },
      body: JSON.stringify(payload)
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error fetching session exercise plans:', error)
    throw new Error(`Failed to fetch session exercise plans: ${error.message}`)
  }
}

/**
 * Fetch exercise plans from API (legacy function - now uses session-based API)
 * @param {Object} params - Query parameters including sessionId and clientId
 * @returns {Promise<Array>} Array of exercise plan objects
 */
export const fetchExercisePlans = async (params = {}) => {
  try {
    if (params.sessionId && params.clientId) {
      return await fetchSessionExercisePlans(params.sessionId, params.clientId)
    }

    throw new Error('Missing sessionId or clientId for fetching exercise plans')
  } catch (error) {
    console.error('Error fetching exercise plans:', error)
    throw new Error(`Failed to fetch exercise plans: ${error.message}`)
  }
}

/**
 * Create a new exercise plan
 * @param {Object} planData - Plan data to create
 * @returns {Promise<Object>} Created plan object
 */
export const createExercisePlan = async (planData) => {
  try {
    // TODO: Replace with actual API call
    // const response = await fetch('/api/exercise-plans', {
    //   method: 'POST',
    //   headers: {
    //     'Content-Type': 'application/json',
    //     'Authorization': `Bearer ${getAuthToken()}`
    //   },
    //   body: JSON.stringify(planData)
    // })
    // 
    // if (!response.ok) {
    //   throw new Error(`HTTP error! status: ${response.status}`)
    // }
    // 
    // const data = await response.json()
    // return data

    // For now, return mock created plan
    await new Promise(resolve => setTimeout(resolve, 500))
    return {
      id: Date.now(),
      ...planData,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }
  } catch (error) {
    console.error('Error creating exercise plan:', error)
    throw new Error(`Failed to create exercise plan: ${error.message}`)
  }
}

/**
 * Update an existing exercise plan
 * @param {number|string} planId - Plan ID to update
 * @param {Object} updates - Updates to apply
 * @returns {Promise<Object>} Updated plan object
 */
export const updateExercisePlan = async (planId, updates) => {
  try {
    // TODO: Replace with actual API call
    // const response = await fetch(`/api/exercise-plans/${planId}`, {
    //   method: 'PUT',
    //   headers: {
    //     'Content-Type': 'application/json',
    //     'Authorization': `Bearer ${getAuthToken()}`
    //   },
    //   body: JSON.stringify(updates)
    // })
    // 
    // if (!response.ok) {
    //   throw new Error(`HTTP error! status: ${response.status}`)
    // }
    // 
    // const data = await response.json()
    // return data

    // For now, return mock updated plan
    await new Promise(resolve => setTimeout(resolve, 500))
    return {
      id: planId,
      ...updates,
      updated_at: new Date().toISOString()
    }
  } catch (error) {
    console.error('Error updating exercise plan:', error)
    throw new Error(`Failed to update exercise plan: ${error.message}`)
  }
}

/**
 * Delete an exercise plan
 * @param {number|string} planId - Plan ID to delete
 * @returns {Promise<boolean>} Success status
 */
export const deleteExercisePlan = async (planId) => {
  try {
    // TODO: Replace with actual API call
    // const response = await fetch(`/api/exercise-plans/${planId}`, {
    //   method: 'DELETE',
    //   headers: {
    //     'Authorization': `Bearer ${getAuthToken()}`
    //   }
    // })
    // 
    // if (!response.ok) {
    //   throw new Error(`HTTP error! status: ${response.status}`)
    // }
    // 
    // return true

    // For now, return success after delay
    await new Promise(resolve => setTimeout(resolve, 500))
    return true
  } catch (error) {
    console.error('Error deleting exercise plan:', error)
    throw new Error(`Failed to delete exercise plan: ${error.message}`)
  }
}

